import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowRight, Instagram, Linkedin, Sparkles, Twitter } from 'lucide-react';
import { socialLinks } from '../data/socialLinks';

const GROWTH_VISUAL = 'https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/356859dd912b2c274c9efed118c2e00eebe8cf5a2a2293a14d9d74e82cc444b4.png';

const FinalCTA = ({ onOpenAssessment, onOpenPlans, onOpenJoin }) => {
  const [ref, visible] = useScrollReveal();

  return (
    <section
      id="contact"
      ref={ref}
      style={{ padding: '100px 24px', background: '#F9F9F7', position: 'relative', overflow: 'hidden' }}
      data-testid="final-cta-section"
    >
      {/* Premium ambient background */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 60%, rgba(42,157,143,0.07) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div
          className={`reveal ${visible ? 'visible' : ''}`}
          style={{
            background: 'rgba(255,255,255,0.78)',
            backdropFilter: 'blur(24px)',
            border: '1.5px solid rgba(42,157,143,0.18)',
            borderRadius: '36px',
            overflow: 'hidden',
            boxShadow: '0 32px 100px rgba(42,157,143,0.14), 0 8px 32px rgba(0,0,0,0.06)',
            display: 'grid',
            gridTemplateColumns: '1fr 420px',
          }}
        >
          {/* Left: Text */}
          <div style={{ padding: '72px 64px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '28px' }}>
              <Sparkles size={13} color="#2A9D8F" />
              <span style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#2A9D8F' }}>
                Begin the Journey
              </span>
            </div>

            <h2 style={{
              fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(32px,4vw,52px)',
              color: '#1A202C', margin: '0 0 20px', letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>
              Start with insight.<br />
              <span style={{ color: '#2A9D8F', fontWeight: 300 }}>Grow with guidance.</span>
            </h2>

            <p style={{
              fontFamily: 'Inter', fontSize: '16px', color: '#64748B',
              lineHeight: 1.75, margin: '0 0 40px', maxWidth: '420px',
            }}>
              Help your child discover direction with confidence. Join families using MindVrik for career discovery, stream selection, aptitude mapping, and guided psychological insight.
            </p>

            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <button className="btn-primary" data-testid="final-start-assessment-btn" style={{ fontSize: '15px', padding: '15px 36px' }} onClick={() => onOpenAssessment?.()}>
                Start Assessment <ArrowRight size={17} />
              </button>
              <button className="btn-ghost" data-testid="final-explore-plans-btn" style={{ fontSize: '15px', padding: '14px 36px' }} onClick={() => onOpenPlans?.()}>
                Explore Plans
              </button>
            </div>

            {/* Trust microline */}
            <div style={{ display: 'flex', gap: '24px', marginTop: '36px', flexWrap: 'wrap' }}>
              {['No commitment required', 'Results in 24 hours', 'Expert-reviewed'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#2A9D8F' }} />
                  <span style={{ fontSize: '12px', color: '#64748B', fontFamily: 'Inter' }}>{t}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginTop: '26px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748B' }}>Follow MindVrik:</span>
              {[
                { icon: <Twitter size={15} />, ...socialLinks.x },
                { icon: <Linkedin size={15} />, ...socialLinks.linkedin },
                { icon: <Instagram size={15} />, ...socialLinks.instagram },
              ].map(({ icon, label, href, handle }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#2A9D8F', fontSize: '12px', fontWeight: 700, textDecoration: 'none' }}>
                  {icon}{handle || label}
                </a>
              ))}
            </div>
          </div>

          {/* Right: Visual */}
          <div style={{ position: 'relative', overflow: 'hidden', minHeight: '400px' }}>
            <img
              src={GROWTH_VISUAL}
              alt="Student career growth and guided planning"
              loading="lazy"
              decoding="async"
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                objectPosition: 'center',
                filter: 'saturate(0.9) brightness(1.05)',
              }}
            />
            {/* Overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to right, rgba(255,255,255,0.4), transparent)',
            }} />
            {/* Floating stat card */}
            <div style={{
              position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
              borderRadius: '16px', padding: '16px 24px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
              textAlign: 'center', whiteSpace: 'nowrap',
            }}>
              <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '28px', color: '#2A9D8F', lineHeight: 1 }}>2,400+</div>
              <div style={{ fontSize: '12px', color: '#64748B', fontFamily: 'Inter', marginTop: '4px' }}>Students guided & growing</div>
            </div>
          </div>
        </div>

        {/* Counselor join CTA */}
        <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginTop: '48px' }}>
          <p style={{ fontSize: '14px', color: '#94A3B8', marginBottom: '16px' }}>
            Are you a counselor or institution looking to partner?
          </p>
          <button className="btn-ghost" data-testid="join-as-counselor-cta" style={{ fontSize: '14px' }} onClick={() => onOpenJoin?.()}>
            Join Our Organisation <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
